import React, { useEffect, useRef, useState } from 'react';
import { getData } from '../../helpers/indexedDbHelper';
import { useRefresh } from '../../hooks/useRefresh';
import LayoutContent from '../../components/layouts/content2';
import { useTranslation } from 'react-i18next';
import DetailProduct from './DetailProduct';
import { useSelector } from 'react-redux';

import HomeScreen from './home';
const ACCENT = '#ED3E08';
const SLIDE_MS = 6000;

// Promos del welcome: se cargan solas desde ./assets/carrousel (orden alfabetico del archivo)
const slidesContext = require.context('./assets/carrousel', false, /\.(png|jpe?g|webp)$/);
const SLIDES = slidesContext.keys().sort().map((key) => slidesContext(key));

const WELCOME_CSS = `
@keyframes welcomePulseGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(237, 62, 8, 0.55), 0 0 18px 3px rgba(237, 62, 8, 0.45); }
    50% { box-shadow: 0 0 0 11px rgba(237, 62, 8, 0), 0 0 32px 7px rgba(237, 62, 8, 0.75); }
}
@keyframes welcomeShine {
    0% { transform: translateX(-120%) skewX(-20deg); }
    60%, 100% { transform: translateX(240%) skewX(-20deg); }
}
`;

function WelcomeCarousel({ onEnter, langEng }) {
    const [index, setIndex] = useState(0);
    const touchStart = useRef(null);
    const swiped = useRef(false);

    useEffect(() => {
        const timer = setInterval(() => {
            setIndex((prev) => (prev + 1) % SLIDES.length);
        }, SLIDE_MS);

        return () => clearInterval(timer);
    }, [index]);

    const goTo = (e, next) => {
        e.stopPropagation();
        setIndex(next);
    };

    const onTouchStart = (e) => {
        swiped.current = false;
        touchStart.current = e.touches[0].clientX;
    };

    const onTouchEnd = (e) => {
        if (touchStart.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStart.current;
        touchStart.current = null;

        if (Math.abs(delta) < 40) return;

        swiped.current = true;
        setIndex((prev) => (prev + (delta < 0 ? 1 : SLIDES.length - 1)) % SLIDES.length);
    };

    return (
        <div
            onClick={() => {
                if (swiped.current) {
                    swiped.current = false;
                    return;
                }
                onEnter();
            }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 99999,
                background: '#000',
                overflow: 'hidden',
                cursor: 'pointer',
                userSelect: 'none',
                WebkitTapHighlightColor: 'transparent'
            }}
        >
            <style>{WELCOME_CSS}</style>

            {/* Sliders a pantalla completa con cross-fade */}
            {SLIDES.map((src, i) => (
                <div
                    key={i}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: i === index ? 1 : 0,
                        transition: 'opacity 900ms ease-in-out',
                        pointerEvents: 'none'
                    }}
                >
                    <img
                        src={src}
                        alt={`promo-${i + 1}`}
                        onContextMenu={(e) => e.preventDefault()}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center',
                            display: 'block'
                        }}
                    />
                </div>
            ))}

            {/* Degradado inferior para legibilidad del boton */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.18) 0%, transparent 35%, rgba(0,0,0,0.55) 78%, rgba(0,0,0,0.9) 100%)',
                    pointerEvents: 'none'
                }}
            />

            {/* Indicadores + boton luminoso */}
            <div
                style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 26,
                    padding: '0 24px 8vh'
                }}
            >
                <div style={{ display: SLIDES.length > 1 ? 'flex' : 'none', gap: 10 }}>
                    {SLIDES.map((_, i) => (
                        <div
                            key={i}
                            onClick={(e) => goTo(e, i)}
                            style={{
                                width: i === index ? 30 : 10,
                                height: 10,
                                borderRadius: 6,
                                background: i === index ? ACCENT : 'rgba(255,255,255,0.45)',
                                boxShadow: i === index ? '0 0 12px 2px rgba(237,62,8,0.7)' : 'none',
                                transition: 'all 350ms ease',
                                cursor: 'pointer'
                            }}
                        />
                    ))}
                </div>

                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onEnter();
                    }}
                    style={{
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 9,
                        padding: '12px 26px',
                        border: '1.5px solid rgba(255,255,255,0.35)',
                        borderRadius: 999,
                        background: `linear-gradient(135deg, ${ACCENT} 0%, #C70100 100%)`,
                        color: '#fff',
                        fontSize: 16,
                        fontWeight: 900,
                        letterSpacing: '0.02em',
                        textTransform: 'uppercase',
                        cursor: 'pointer',
                        animation: 'welcomePulseGlow 1.8s ease-in-out infinite',
                        WebkitTapHighlightColor: 'transparent'
                    }}
                >
                    <span style={{ position: 'relative', zIndex: 2 }}>
                        {langEng ? 'Tap to view menu' : 'Toca para ver el menú'}
                    </span>
                    <span style={{ position: 'relative', zIndex: 2, opacity: 0.6, fontWeight: 400 }}>/</span>
                    <span style={{ position: 'relative', zIndex: 2, fontSize: 13, opacity: 0.9 }}>
                        {langEng ? 'Toca para ver el menú' : 'Tap to view menu'}
                    </span>

                    {/* Brillo que recorre el boton */}
                    <span
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '35%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)',
                            animation: 'welcomeShine 2.8s ease-in-out infinite',
                            pointerEvents: 'none',
                            zIndex: 1
                        }}
                    />
                </button>
            </div>
        </div>
    );
}



function Index() {

    const {i18n} = useTranslation();
    const langEng = i18n.language === 'en' ?? false;
    const { change, getInitData} = useRefresh();
    const [showDetailProduct, setShowDetailProduct] = useState(false);
     const [showListProduct, setShowListProduct] = useState(false);
    const [dataDish, setDataDish] = useState(false);
    const [showHome, setShowHome] = useState(false);
    const [menus, setMenus] = useState([]);
 const [valentine, setvalentine] = useState([]);
 const [datakarol, setdatakarol] = useState(false);
     const {
       goHomeInit
     } = useSelector((state) => state.initLoad);
    
    
       useEffect(() => {
      
    }, [change]);



    useEffect(() => {
    if (valentine) {
      valentine.map((item) => {
       
          if (item.id === 5494) {
          setdatakarol(item);
          }
         
      });
    }
    }, [valentine]);
    

     useEffect(() => {
            console.log(goHomeInit, 'q mas qlq')
              setShowHome(false)
     }, [goHomeInit]);
    
    useEffect(() => {
        const handleVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
            console.log("La página ha perdido el foco (está oculta).");
           setShowHome(false)
            getInitData()

        } else if (document.visibilityState === "visible") {
            console.log("La página está en foco (visible).");
        }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        // Limpieza del evento al desmontar el componente
        return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    window.onerror = function () {
        console.log('Error global detectado, recargando...');
        setTimeout(() => {
            window.location.reload();
        }, 2000);
        };
    

    return (
        <LayoutContent  showBack={false} showFooter={false} showLogo={false} showImageTop={false} setShowSpecials={() => {
            
        }} goCategories={null}>
            {!showHome && (
                <WelcomeCarousel
                    langEng={langEng}
                    onEnter={() => setShowHome(true)}
                />
            )}

            {showDetailProduct && <DetailProduct
                    dishMenu={dataDish}
                    setShowListProduct2={setShowListProduct}
                    setShowDetailProduct={setShowDetailProduct}
            />}

             {showHome && <HomeScreen  />}
            
        </LayoutContent>
    );
}

export default Index;